/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'
import { isEmpty } from 'lodash'

import { EuiFlexGroup, EuiFlexItem, EuiIcon, EuiSkeletonText } from '@elastic/eui'

import { useGetPaymentDetailsV1 } from '@modules/billing-lib/hooks'
import PrivacySensitiveContainer from '@modules/cui/PrivacySensitiveContainer'
import type { RecurlyBillingInfo } from '@modules/ui-types/billing'

interface Props {
  organizationId: string
}

const CreditCardDetails = ({ organizationId }: Props) => {
  const { data: { recurly_billing_info = {} } = {}, isLoading } =
    useGetPaymentDetailsV1(organizationId)

  const { first_name, last_name, last_four, month, year } =
    recurly_billing_info as RecurlyBillingInfo

  if (isLoading) {
    return <EuiSkeletonText lines={1} />
  }

  if (isEmpty(recurly_billing_info)) {
    return null
  }

  return (
    <PrivacySensitiveContainer>
      <EuiFlexGroup
        alignItems='center'
        gutterSize='m'
        data-test-id='billing-cc-details'
        responsive={false}
      >
        <EuiFlexItem grow={false}>
          {first_name} {last_name}
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup gutterSize='s' alignItems='center' responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiIcon type='payment' size='l' />
            </EuiFlexItem>

            <EuiFlexItem grow={false}>
              {
                '\u2022\u2022\u2022\u2022\u00a0\u2022\u2022\u2022\u2022\u00a0\u2022\u2022\u2022\u2022\u00a0'
              }
              {last_four}
            </EuiFlexItem>
            <EuiFlexItem grow={false}>{`${month}/${year}`}</EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
    </PrivacySensitiveContainer>
  )
}

export default CreditCardDetails
